import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";

import { useSession } from "../../../features/auth/client";
import { usePermission } from "../../../features/auth/use-permission";
import { HistoryTabs } from "../../../features/change-history/components/history-tabs";
import { RecordHistory } from "../../../features/change-history/components/record-history";
import { historyTabSearchSchema } from "../../../features/change-history/history-search";
import { facilityPickQueryOptions } from "../../../features/facility-pick/api/facilities";
import { roleAssignmentsQueryOptions } from "../../../features/role-assignments/api/role-assignments";
import { RoleAssignmentAdmin } from "../../../features/role-assignments/components/role-assignment-admin";

/**
 * `RPM-092`'s Roles and permissions (`J-296`): the list, an assignment's detail, the grant form
 * `J-270` carried (`RPM-093`) and the Until `J-271` carried (`RPM-094`), and the revocation.
 *
 * THE ROUTE COMPOSES FEATURES: the buildings a grant may name are `facility-pick`'s list, the actor is
 * the session's, and the screen is `role-assignments`'. A grant, a change and a revocation ask
 * `user: ["set-role"]` at the tenant's three scopes, as the server does; the server decides again on
 * every request, and refuses a change to the actor's own assignment whatever the screen showed.
 * Each assignment's History (`RPM-063`) is read at tenant scope, for an account holding
 * `audit: ["read"]` there.
 */
function RolesScreen() {
  const { tab = "details" } = Route.useSearch();
  const navigate = Route.useNavigate();
  const { data: session } = useSession();
  const canGrant = usePermission({ user: ["set-role"] });
  const canReadHistory = usePermission({ audit: ["read"] }, ["TENANT"]);
  const facilities = useQuery(facilityPickQueryOptions());
  const assignments = useQuery({ ...roleAssignmentsQueryOptions(), enabled: canReadHistory });
  return (
    <div className="flex flex-col gap-4 pt-4">
      <HistoryTabs
        showHistory={canReadHistory}
        tab={tab}
        onTabChange={(next) =>
          void navigate({ search: (prev) => ({ ...prev, tab: next }), replace: true })
        }
        detailsLabel="Roles and permissions"
        details={
          <RoleAssignmentAdmin
            canGrant={canGrant}
            actorId={session?.user.id ?? ""}
            actorName={session?.user.name ?? ""}
            facilities={(facilities.data?.items ?? []).map(({ facilityId, name }) => ({
              facilityId,
              name,
            }))}
          />
        }
        history={
          <RecordHistory
            entity="role-assignments"
            records={(assignments.data?.roleAssignments ?? []).map((a) => ({
              id: a.roleAssignmentId,
              label: `${a.holderName}, ${a.roleCode}`,
            }))}
            noun="Assignment"
            caption={(label) => `Change history of the assignment ${label}`}
            columnLabels={{ valid_from: "From", valid_to: "Until", superseded_at: "Revoked" }}
          />
        }
      />
    </div>
  );
}

export const Route = createFileRoute("/_authenticated/admin/roles")({
  validateSearch: historyTabSearchSchema,
  component: RolesScreen,
  staticData: { screenName: "Roles and permissions" },
});
